import { Container, Text, type Texture } from 'pixi.js';
import type { PlayerProfile } from '../../model/profile/PlayerProfile';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';

/** What the host does for settings the slot can't do itself. */
export interface SlotSettingsHost {
  /** Push notifications, when this player can have them. */
  push?: { enabled(): Promise<boolean>; set(on: boolean): Promise<boolean> } | null;
  /** Restore purchases (the app stores), when there are any to restore. */
  restore?: (() => Promise<void>) | null;
  /** Royal Road's story is on for the player: show the Story section. */
  story?: (() => boolean) | null;
  /** The optional account. `status()` is read each time the board opens:
   *  null while accounts are off (no row), else the address signed in
   *  with, or null for none yet. `open()` shows the account's own board. */
  account?: { status(): { email: string | null } | null; open(): void } | null;
  /** At a wheel table: the reel skins to choose from and the one in use
   *  (the row shows when there are two or more). */
  skins?: { list(): readonly { id: string; name: string }[]; selected(): string; select(id: string): void } | null;
}

/** An ON / OFF switch drawn as the kit's buttons: gold when on, wood when off. */
class Switch extends Container {
  private readonly onButton: BrassButton;
  private readonly offButton: BrassButton;
  private value = false;
  private busy = false;

  constructor(private readonly changed: (on: boolean) => Promise<boolean> | boolean) {
    super();
    this.onButton = new BrassButton({ label: 'ON', kind: 'gold', size: 16, width: 84, height: 40, onTap: () => void this.toggle() });
    this.offButton = new BrassButton({ label: 'OFF', kind: 'wood', size: 16, width: 84, height: 40, onTap: () => void this.toggle() });
    this.addChild(this.onButton, this.offButton);
    this.set(false);
  }

  get state(): boolean {
    return this.value;
  }

  set(on: boolean): void {
    this.value = on;
    this.onButton.visible = on;
    this.offButton.visible = !on;
  }

  async toggle(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.set(await this.changed(!this.value));
    } finally {
      this.busy = false;
    }
  }
}

interface Row {
  label: Text;
  line: Text;
  control: Container;
  /** Shown only while this holds (the story's rows). */
  when?: () => boolean;
  /** The title of the group this row starts. */
  section?: Text;
}

/**
 * The slot's settings, on a board (producer 2026-10-14: no plain dialogs):
 * music, sound effects and the double-or-nothing offer, and, where they
 * apply, push notifications and restoring purchases; while accounts are on,
 * the account; for a player with the story, Fen (off is Classic mode) and
 * how often he speaks. Each switch is a pair
 * of the kit's buttons, ON in gold and OFF in wood. DONE (or the dim)
 * closes it.
 */
export class SettingsBoard extends BoardDialog {
  private readonly rows: Row[] = [];
  /** A group's title, waiting for the group's first row. */
  private pendingSection: Text | null = null;
  private readonly music: Switch;
  private readonly sfx: Switch;
  private readonly gamble: Switch;
  private readonly push: Switch | null = null;
  private readonly fen: Switch;
  private readonly chatter: Switch;
  private readonly account: Row | null = null;
  private readonly skin: BrassButton | null = null;
  /** The skin on now (as last chosen here). */
  private skinId = '';
  /** The account as the board last opened with it; null hides its row. */
  private accountStatus: { email: string | null } | null = null;
  private readonly done: BrassButton;
  private closed: (() => void) | null = null;
  private arranging = false;

  constructor(
    private readonly profile: PlayerProfile,
    private readonly host: SlotSettingsHost,
    private readonly floor: () => Texture | null,
  ) {
    super({ title: 'SETTINGS', floor: floor(), maxWidth: 420, titleSize: 18 });
    this.music = new Switch((on) => {
      profile.setMusicEnabled(on);
      return profile.musicEnabled;
    });
    this.sfx = new Switch((on) => {
      profile.setSfxEnabled(on);
      return profile.sfxEnabled;
    });
    this.gamble = new Switch((on) => {
      profile.setGambleEnabled(on);
      return profile.gambleEnabled;
    });
    this.section('SOUND');
    this.row('Music', 'Background music and the sounds of the place', this.music);
    this.row('Sound effects', 'Spins, wins, taps and coins', this.sfx);
    this.section('GAME');
    this.row('Gamble', 'Offer double or nothing after a win', this.gamble);
    const skins = host.skins;
    if (skins) {
      // A tap moves to the next skin; the button says which is on.
      const button = new BrassButton({ label: '', kind: 'wood', size: 14, width: 150, height: 40 });
      button.onTap = () => {
        const list = skins.list();
        const next = list[(list.findIndex((s) => s.id === this.skinId) + 1) % Math.max(1, list.length)];
        if (!next) return;
        this.skinId = next.id;
        skins.select(next.id);
        button.setLabel(next.name.toUpperCase());
      };
      this.skin = button;
      this.row('Reel skin', 'How the reels look at this table. Tap to change', button, () => skins.list().length >= 2);
    }
    const account = host.account;
    if (account) {
      // The account's own board opens once this one has gone.
      const button = new BrassButton({ label: 'OPEN', kind: 'wood', size: 15, width: 120, height: 40 });
      button.onTap = () => void this.dismiss().then(() => account.open());
      this.section('ACCOUNT');
      this.account = this.row(
        'Play on any device',
        'Create an account or sign in with your email',
        button,
        () => this.accountStatus !== null,
      );
      // An address has no spaces to wrap at.
      this.account.line.style.breakWords = true;
    }
    this.fen = new Switch((on) => {
      profile.setStoryPrefs({ storyMode: on ? 'on' : 'off' });
      this.arrange();
      return profile.storyMode !== 'off';
    });
    this.chatter = new Switch((on) => {
      profile.setStoryPrefs({ fenChatter: on ? 'often' : 'big' });
      return profile.fenChatter === 'often';
    });
    const story = () => host.story?.() ?? false;
    this.section('STORY');
    this.row('Fen the fox', 'Your host on the road. Off is Classic mode', this.fen, story);
    this.row('Fen speaks often', 'Off, he speaks only at big moments', this.chatter, () => story() && profile.storyMode !== 'off');
    const push = host.push;
    if (push) {
      this.push = new Switch((on) => push.set(on));
      this.row('Notifications', 'Tell me when a friend joins my table', this.push);
    }
    const restore = host.restore;
    if (restore) {
      const button = new BrassButton({ label: 'RESTORE', kind: 'wood', size: 15, width: 120, height: 40 });
      button.onTap = () => {
        button.setEnabled(false);
        button.setLabel('...');
        void restore().finally(() => {
          button.setEnabled(true);
          button.setLabel('RESTORE');
        });
      };
      this.row('Restore purchases', 'Bring back gems bought on another device', button);
    }
    this.done = new BrassButton({ label: 'DONE', kind: 'gold', size: 17, onTap: () => void this.dismiss() });
    this.content.addChild(this.done);
    this.onDimTap = () => void this.dismiss();
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): {
    open: boolean;
    switches: Record<string, boolean>;
    account: { label: string; line: string } | null;
    skin: string | null;
  } {
    const story = this.host.story?.() ?? false;
    const account =
      this.account && this.accountStatus !== null ? { label: this.account.label.text, line: this.account.line.text } : null;
    return {
      open: this.visible,
      switches: {
        music: this.music.state,
        sfx: this.sfx.state,
        gamble: this.gamble.state,
        ...(this.push ? { push: this.push.state } : {}),
        ...(story ? { fen: this.fen.state, ...(this.fen.state ? { chatter: this.chatter.state } : {}) } : {}),
      },
      account,
      skin: this.skin && (this.host.skins?.list().length ?? 0) >= 2 ? this.skin.labelText : null,
    };
  }

  /** Tap the account row's button, as a player does (browser tests). */
  openAccount(): boolean {
    const button = this.account && this.accountStatus !== null ? this.account.control : null;
    if (!(button instanceof BrassButton)) return false;
    button.onTap?.();
    return true;
  }

  /** Flip a switch by name, as a tap does (browser tests). */
  toggle(name: 'music' | 'sfx' | 'gamble' | 'fen' | 'chatter'): Promise<void> {
    return this[name].toggle();
  }

  /** Show the settings as they stand; resolves when it closes. */
  async play(): Promise<void> {
    this.music.set(this.profile.musicEnabled);
    this.sfx.set(this.profile.sfxEnabled);
    this.gamble.set(this.profile.gambleEnabled);
    this.fen.set(this.profile.storyMode !== 'off');
    this.chatter.set(this.profile.fenChatter === 'often');
    if (this.push && this.host.push) void this.host.push.enabled().then((on) => this.push?.set(on));
    const skins = this.host.skins;
    if (this.skin && skins) {
      this.skinId = skins.selected();
      this.skin.setLabel((skins.list().find((s) => s.id === this.skinId)?.name ?? '').toUpperCase());
    }
    if (this.account) {
      this.accountStatus = this.host.account?.status() ?? null;
      this.account.label.text = this.accountStatus?.email ? 'Signed in' : 'Play on any device';
      this.account.line.text = this.accountStatus?.email ?? 'Create an account or sign in with your email';
    }
    this.setFloor(this.floor());
    this.arrange();
    const closed = new Promise<void>((resolve) => (this.closed = resolve));
    await this.open();
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private async dismiss(): Promise<void> {
    const done = this.closed;
    if (!done) return;
    this.closed = null;
    await this.close();
    done();
  }

  private section(title: string): void {
    const text = new Text({ text: title, style: displayStyle(14) });
    this.content.addChild(text);
    this.pendingSection = text;
  }

  private row(label: string, line: string, control: Container, when?: () => boolean): Row {
    const row: Row = {
      label: new Text({ text: label, style: uiStyle(16, COLOURS.cream) }),
      line: new Text({ text: line, style: { ...uiStyle(12, COLOURS.cream, '500'), wordWrap: true } }),
      control,
      when,
      section: this.pendingSection ?? undefined,
    };
    this.pendingSection = null;
    row.line.alpha = 0.85;
    this.content.addChild(row.label, row.line, control);
    this.rows.push(row);
    return row;
  }

  private arrange(): void {
    if (this.arranging || !this.done) return;
    this.arranging = true;
    const w = this.contentWidth;
    let y = 0;
    let first = true;
    this.rows.forEach((row) => {
      const shown = row.when?.() ?? true;
      row.label.visible = row.line.visible = row.control.visible = shown;
      if (row.section) row.section.visible = shown;
      if (!shown) return;
      // Each group's title over its first row.
      if (row.section) {
        row.section.position.set(0, y + (first ? 0 : 6));
        y += row.section.height + (first ? 6 : 12);
      }
      first = false;
      const controlW = row.control.width;
      row.line.style.wordWrapWidth = Math.max(120, w - controlW - 14);
      const textH = row.label.height + row.line.height;
      const h = Math.max(46, textH + 6);
      row.label.position.set(0, y + (h - textH) / 2);
      row.line.position.set(0, row.label.y + row.label.height);
      row.control.position.set(w - controlW, y + (h - 40) / 2);
      y += h;
    });
    y += 14;
    const bw = Math.min(w, 200);
    this.done.resize(bw, 46);
    this.done.position.set((w - bw) / 2, y);
    y += 50;
    this.setContentHeight(y);
    this.arranging = false;
  }
}
